import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBell, FaCoins } from "react-icons/fa";
import { useGlobalContext } from "../pages/AUTH/GlobalContext";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [coins, setCoins] = useState(0);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isLoggedIn, user, setIsLoggedIn, setUser, loading } = useGlobalContext();

  // Role and status checks
  const showAdminLink =
    user?.email?.toLowerCase() === "rohitshekrsingh@gmail.com";
  const isUser = user?.role === "user";
  const isMentorApproved = user?.mentorStatus === "approved";
  const isRecruiterApproved = user?.recruiterStatus === "Approved";

  const handleSignOut = () => {
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem("jwtToken");
    navigate("/login");
  };

  const handleProfileClick = () => navigate("/profile");

  const fetchNotifications = async () => {
    try {
      setLoadingNotif(true);
      const token = localStorage.getItem("jwtToken");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/notifications`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.notifications) {
          setNotifications(result.data.notifications);
        } else {
          setNotifications([]);
        }
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
    } finally {
      setLoadingNotif(false);
    }
  };

  const fetchCoins = async () => {
    try {
      setLoadingNotif(true);
      const token = localStorage.getItem("jwtToken");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/coins/balance`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCoins(result.data.balance);
        } else {
          setCoins(0);
        }
      } else {
        setCoins(0);
      }
    } catch (error) {
      console.error("Error fetching coins:", error);
      setCoins(0);
    } finally {
      setLoadingNotif(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications();
      fetchCoins();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasUnreadNotifications = notifications.some((n) => !n.isRead);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/hired.png" alt="Hired" className="h-12 cursor-pointer" />

          {user?.isPremium && (
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow">
              PREMIUM
            </span>
          )}
        </Link>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-gray-700"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Nav Links */}
        <div
          className={`${
            isMenuOpen ? "flex" : "hidden"
          } lg:flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-6 absolute lg:static top-full left-0 right-0 bg-white lg:bg-transparent border-b border-gray-100 lg:border-0 shadow-lg lg:shadow-none p-4 lg:p-0 z-40`}
        >
          {isLoggedIn ? (
            <>
              {/* In-app links for every logged-in user, admins included */}
              <Link
                to="/overview"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Dashboard
              </Link>
              <Link
                to="/tools"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Tools
              </Link>
              <Link
                to="/interviews"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Interviews
              </Link>
              <Link
                to="/resume-upload"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Resume
              </Link>

              {/* Admin accounts additionally get the Admin link */}
              {showAdminLink && (
                <Link
                  to="/AdminDashboard"
                  className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
                >
                  Admin
                </Link>
              )}

              {/* Mentor Dashboard & Add Job - Only for approved mentors */}
              {isMentorApproved && (
                <>
                  <Link
                    to="/MentorDashboard-interview"
                    className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
                  >
                    Mentor Dashboard
                  </Link>
                  <Link
                    to="/post-job"
                    className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
                  >
                    Add Job
                  </Link>
                </>
              )}
            </>
          ) : (
            /* Links visible to logged-out visitors */
            <>
              <Link
                to="/"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Home
              </Link>
              <Link
                to="/jobs"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Jobs
              </Link>
              <Link
                to="/blogs"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Blogs
              </Link>
              <Link
                to="/pricing"
                className="block lg:inline-block text-gray-700 text-sm font-medium hover:text-blue-600 py-2 lg:py-0"
              >
                Pricing
              </Link>
            </>
          )}
        </div>

        {/* Right Section */}
        <div className="hidden lg:flex items-center space-x-4 relative">
          {isLoggedIn ? (
            <>
              {/* ================= NOTIFICATION ================= */}
              <div className="relative">
                <button
                  onClick={() => setIsNotifOpen(!isNotifOpen)}
                  className="h-12 w-12 flex items-center justify-center rounded-full border bg-white hover:bg-gray-100 relative"
                >
                  <FaBell className="w-6 h-6 text-blue-500" />

                  {/* Number Badge */}
                  {hasUnreadNotifications && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {isNotifOpen && (
                  <div
                    className="absolute right-0 mt-3 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 
               animate-[fadeIn_0.15s_ease-out]"
                    style={{ animationFillMode: "forwards" }}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-t-xl">
                      <h3 className="text-sm font-semibold tracking-wide">
                        Notifications
                      </h3>
                      <button
                        onClick={fetchNotifications}
                        className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition"
                      >
                        {loadingNotif ? "Loading..." : "Refresh"}
                      </button>
                    </div>

                    {/* List */}
                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                      {loadingNotif ? (
                        <div className="p-6 text-center text-gray-500">
                          Loading...
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-6 text-center text-gray-400">
                          <img
                            src="/empty-bell.png"
                            alt=""
                            className="h-16 mx-auto mb-3 opacity-70"
                          />
                          No notifications yet
                        </div>
                      ) : (
                        notifications.map((notif, i) => (
                          <div
                            key={notif._id || i}
                            className={`px-5 py-4 transition group hover:bg-gray-50 cursor-pointer
                        ${!notif.isRead ? "bg-blue-50" : "bg-white"}`}
                          >
                            <div className="flex items-start space-x-3">
                              {/* Unread dot */}
                              {!notif.isRead && (
                                <span className="h-2.5 w-2.5 bg-blue-500 rounded-full mt-1.5"></span>
                              )}

                              <div className="flex-1">
                                <p className="text-sm text-gray-700 group-hover:text-gray-900">
                                  {notif.message || "Notification"}
                                </p>

                                {notif.timestamp && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notif.timestamp).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ================= COINS ================= */}
              <div className="h-12 w-12 flex flex-col items-center justify-center rounded-full border bg-white select-none cursor-default">
                <FaCoins className="w-5 h-5 text-yellow-500" />
                <span className="text-xs font-semibold">{coins}</span>
              </div>

              {/* ================= PROFILE AVATAR ================= */}
              <div className="relative">
                {loading ? (
                  <div className="h-12 w-12 rounded-full border bg-gray-200 animate-pulse" />
                ) : (
                  <img
                    src={user?.picture || "/default-avatar.png"}
                    alt="User Avatar"
                    className="h-12 w-12 rounded-full border object-cover cursor-pointer bg-white"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  />
                )}

                {isDropdownOpen && !loading && (
                  <div
                    ref={dropdownRef}
                    className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-md py-2 z-50"
                  >
                    <button
                      onClick={handleProfileClick}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      View Profile
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                className="text-gray-700 text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl shadow-sm shadow-blue-600/20 hover:bg-blue-700 transition-colors"
                onClick={() => navigate("/register")}
              >
                Register
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
